'use client'
import React, { FC, useCallback, useState } from 'react'
import {
  Avatar,
  Button,
  Heading,
  HStack,
  Icon,
  StackDivider,
  StackProps,
  Text,
  VStack
} from '@chakra-ui/react'
import { FaMobile } from 'react-icons/fa'
import { BrokerDTO } from '@/core/broker/typings'
import { gtmEvent, RealestateDetailBrokerTelephoneClick } from '@/utils/gtm'
import ContactBrokerModal from '@/app/components/modals/ContactBrokerModal'
import { RealEstateDTO, RealEstateLightDTO } from '@/core/real-estate/typings'
import { ImageVariants } from '@/core/media/typings'
import { getDisplayImageUrl } from '@/core/media/utils/get-display-image-url'
import { useClientLocale } from '@/hooks/use-locale-config'

interface RealEstateDetailContactProps extends StackProps {
  brokers: BrokerDTO[]
  realEstate: RealEstateLightDTO | RealEstateDTO
}

const RealEstateDetailContact: FC<RealEstateDetailContactProps> = ({
  brokers,
  realEstate,
  ...props
}) => {
  const locale = useClientLocale()
  if (!brokers.length) return null

  return (
    <VStack
      position="sticky"
      top="120px"
      align="flex-start"
      spacing={6}
      w="full"
      p={8}
      rounded="md"
      maxW="sm"
      bg="background.surface"
      divider={<StackDivider borderColor="gray.400" />}
      flex={1}
      {...props}>
      <Heading as="h5" fontSize="3xl">
        Contacto
      </Heading>

      {brokers.map((broker) => (
        <VStack spacing={6} align="center" key={broker.id} w="full">
          <Avatar
            src={getDisplayImageUrl(broker.img, ImageVariants['600x600'])}
            h={'80px'}
            w={'80px'}
          />
          <HStack spacing={4} w="full">
            <VStack spacing={2} flex={1}>
              <Heading as="h6" fontSize="lg">
                {broker.name}
              </Heading>

              <VStack justify="flex-start" align="center" w="full">
                <ContactRevealField
                  label="Teléfono"
                  icon={FaMobile}
                  data={broker.phone}
                  gtmEvent={() =>
                    gtmEvent<RealestateDetailBrokerTelephoneClick>({
                      name: 'realestate_detail_broker_telephone_click',
                      params: { broker_id: broker.id },
                      locale
                    })
                  }
                  linkType="tel"
                />
              </VStack>
            </VStack>
          </HStack>
          <ContactBrokerModal
            trigger={
              <Button
                rounded="md"
                _hover={{ bg: 'primary.500' }}
                w="full"
                bg="primary"
                color="text.invert">
                Contactar para detalles
              </Button>
            }
            realEstate={realEstate}
            broker={broker}
          />
        </VStack>
      ))}
    </VStack>
  )
}

const ContactRevealField: FC<{
  label: string
  icon: any
  data: string
  gtmEvent: () => void
  linkType: 'tel' | 'mailto'
}> = ({ label, icon, data, gtmEvent, linkType }) => {
  const [isRevealed, setIsRevealed] = useState(false)

  const handleReveal = useCallback(() => {
    gtmEvent()
    setIsRevealed(true)
  }, [gtmEvent])

  return (
    !isRevealed ? (
      <Button
        justifyContent="center"
        px={0}
        minW="auto"
        variant="ghost"
        leftIcon={<Icon as={icon} boxSize={6} />}
        onClick={handleReveal}>
        <Text as="span" flex={1} fontSize="sm" align="left" color="blue.500">
          {label}
        </Text>
      </Button>
    ) : (
      <HStack justify="center" px={0} minW="auto" spacing={4}>
        <Icon as={icon} boxSize={6} />
        <Text
          flex={1}
          fontSize="sm"
          align="left"
          as="a"
          color="blue.500"
          href={`${linkType}:${data}`}
          data-testid={`contact-${label.toLowerCase()}`}>
          {data}
        </Text>
      </HStack>
    )
  )
}

export default RealEstateDetailContact
